import React, { useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import {
  breakpointSizes,
  fontSize,
  minBreakpointQuery,
  fluidFontSize,
  standardColours,
  visuallyHidden,
} from '../styles';
import { Container, Button, Heading } from './ui';

const StyledNewsletterSignup = styled.section`
  background-color: ${standardColours.black};
  position: relative;
`;

const StyledOuter = styled.div`
  position: relative;
  background: linear-gradient(
    to left,
    ${standardColours.transparent},
    ${standardColours.black}
  );
`;

const StyledImage = styled(GatsbyImage)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  width: 100%;
`;

const StyledInner = styled.div`
  color: ${standardColours.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 125px 0;
  gap: 40px;

  ${minBreakpointQuery.large`
    flex-direction: row;
    justify-content: space-between;
    gap: 40px;
  `}
`;

const StyledHeader = styled.header`
  ${minBreakpointQuery.large`
    max-width: 500px;
  `}
`;

const StyledHeading = styled(Heading)``;

const StyledText = styled.p`
  margin-top: 20px;

  ${fluidFontSize(
    '20px',
    '33px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
`;

const StyledFormWrapper = styled.div``;

const StyledSignupForm = styled.form`
  width: 100%;
  display: grid;
  gap: 10px;

  ${minBreakpointQuery.small`
    display: flex;
    gap: 5px;
  `}
`;

const StyledLabel = styled.label`
  width: 100%;

  span {
    ${visuallyHidden()}
  }
`;

const StyledInput = styled.input`
  background-color: ${standardColours.white};
  border-radius: 3px;
  width: 100%;
  height: 76px;
  padding: 14px;
  border: none;

  ${minBreakpointQuery.small`
    padding: 16px;
  `}

  ${minBreakpointQuery.medium`
    ${fontSize(18)};
    padding: 18px;
  `}

  ${minBreakpointQuery.large`
    ${fontSize(20)};
    padding: 20px;
  `}
`;

const StyledButton = styled(Button)`
  width: 100%;
  height: 76px;

  ${minBreakpointQuery.medium`
    ${fontSize(18)};
    max-width: 320px;
  `}

  ${minBreakpointQuery.large`
    ${fontSize(20)};
    max-width: 360px;
  `}
`;

const StyledGDPRText = styled.p`
  ${fontSize(20)};
  margin-top: 30px;

  ${minBreakpointQuery.small`
    ${fontSize(18)};
    margin-top: 15px;
  `}

  ${minBreakpointQuery.medium`
    ${fontSize(20)};
  `}
`;

const NewsletterSignup = () => {
  const {
    contentfulSitewide: {
      newsletterSignUpBackgroundImage,
      newsletterSignUpHeading,
      newsletterSignUpText,
      newsletterSignUpGdprStatement,
    },
  } = useStaticQuery(graphql`
    query NewsletterSignupQuery {
      contentfulSitewide {
        newsletterSignUpBackgroundImage {
          gatsbyImageData(width: 1880, height: 500)
          title
        }
        newsletterSignUpHeading
        newsletterSignUpText
        newsletterSignUpGdprStatement
      }
    }
  `);

  const [submitted, setSubmitted] = useState(false);

  const submitForm = e => {
    e.preventDefault();

    const newsletterForm = document.getElementById('newsletter');
    const formData = new FormData(newsletterForm);

    const email = formData.get('email');

    fetch('/.netlify/functions/newsletterSignup', {
      method: 'POST',
      body: JSON.stringify({
        email: email,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw response;
        }
        setSubmitted(true);

        window.klaviyo &&
          window.klaviyo
            .identify({
              email: email,
            })
            .then(() =>
              window.klaviyo.track('Completed newsletter sign up form'),
            );
      })
      .catch(error => error.json().then(err => console.log(err.msg)));
  };

  return (
    <StyledNewsletterSignup>
      <StyledImage
        image={newsletterSignUpBackgroundImage.gatsbyImageData}
        alt={newsletterSignUpBackgroundImage.title}
      />
      <StyledOuter>
        <Container>
          <StyledInner>
            <StyledHeader>
              <StyledHeading>{newsletterSignUpHeading}</StyledHeading>
              <StyledText>{newsletterSignUpText}</StyledText>
            </StyledHeader>
            <StyledFormWrapper>
              {!submitted ? (
                <StyledSignupForm
                  id="newsletter"
                  name="newsletter"
                  method="POST"
                  onSubmit={e => {
                    submitForm(e);
                  }}
                >
                  <StyledLabel>
                    <span>Email</span>
                    <StyledInput
                      type="email"
                      name="email"
                      placeholder="Enter your e-mail here"
                    />
                  </StyledLabel>
                  <StyledButton type="submit" hasArrow={true} isAlt={true}>
                    Sign up
                  </StyledButton>
                </StyledSignupForm>
              ) : (
                <StyledText>Thanks for signing up</StyledText>
              )}
              <StyledGDPRText>{newsletterSignUpGdprStatement}</StyledGDPRText>
            </StyledFormWrapper>
          </StyledInner>
        </Container>
      </StyledOuter>
    </StyledNewsletterSignup>
  );
};

export default NewsletterSignup;
